// The UI thread's memory of files THIS tab attached: their bytes as object URLs, so a just-sent
// message draws its pictures from the reader's own copy (no network, no flash) — the tray, the
// echo, the pending row and the sent post alike — and the upload progress the pending row shows.
// Keyed by the tray's local id AND, once uploaded, the server's file id.
//
// Also the echo's attachments: Conversation/ThreadPane paint a message the frame it is sent (an
// echo, before the store has the row); `rememberEchoFiles` lets that echo carry its files.
import { useSyncExternalStore } from 'react';
import type { PostFileRecord } from '../../../core/records';

export interface LocalFile {
  localId: string;
  name: string;
  size: number;
  mimeType: string;
  objectUrl: string | null;
  width: number;
  height: number;
  serverId: string | null;
  /** 0…1 while uploading; 1 when done; -1 when the upload failed. */
  progress: number;
}

const byId = new Map<string, LocalFile>();
const echoes = new Map<string, PostFileRecord[]>();
const listeners = new Set<() => void>();
let version = 0;

function changed(): void {
  version += 1;
  for (const l of listeners) l();
}

export function registerLocal(file: LocalFile): void {
  byId.set(file.localId, file);
  changed();
}

/** Progress / server id / measured size from a status poll. */
export function updateLocal(localId: string, patch: Partial<LocalFile>): void {
  const f = byId.get(localId);
  if (!f) return;
  Object.assign(f, patch);
  if (f.serverId) byId.set(f.serverId, f);
  changed();
}

/** Removed from the tray before sending: the bytes go. */
export function forgetLocal(localId: string): void {
  const f = byId.get(localId);
  if (!f) return;
  byId.delete(localId);
  if (f.serverId) byId.delete(f.serverId);
  if (f.objectUrl) URL.revokeObjectURL(f.objectUrl);
  changed();
}

export function localFile(fileId: string): LocalFile | undefined {
  return byId.get(fileId);
}

/** The reader's own copy of a file, when this tab attached it. */
export function localUrl(fileId: string): string | null {
  return byId.get(fileId)?.objectUrl ?? null;
}

export function isLocalId(fileId: string): boolean {
  return fileId.startsWith('local-');
}

/** Re-render on progress — only components that draw a local file subscribe. */
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};
const subscribeNothing = () => () => undefined;
const readVersion = () => version;
const readZero = () => 0;

export function useLocalFilesVersion(active: boolean): number {
  return useSyncExternalStore(active ? subscribe : subscribeNothing, active ? readVersion : readZero);
}

/** The echo of a send carries its attachments (by the ids the send carries). */
export function rememberEchoFiles(pendingId: string, channelId: string, fileIds: string[] | undefined): void {
  if (!fileIds?.length) return;
  echoes.set(
    pendingId,
    fileIds.map((id, index) => {
      const f = byId.get(id);
      return {
        fileId: id,
        postId: pendingId,
        channelId,
        name: f?.name ?? '',
        size: f?.size ?? 0,
        mimeType: f?.mimeType ?? '',
        width: f?.width ?? 0,
        height: f?.height ?? 0,
        miniPreview: null,
        sortOrder: index,
      };
    }),
  );
  // An echo lives one store round trip; keep the map from growing for the life of the tab.
  if (echoes.size > 50) echoes.delete(echoes.keys().next().value as string);
}

export function echoFiles(postId: string): PostFileRecord[] | undefined {
  return echoes.get(postId);
}
